import z from "zod";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

/**
 * A transfer carrier — the labelled trolley, roll cage or forklift pallet that walks returned
 * material from the goods-in door to the shelf it belongs on.
 *
 * **It is deliberately not a load carrier**, and the difference is cardinality rather than taste.
 * A load carrier is a fungible *type* with a quantity balance — "18 Gitterboxen", with a minimum
 * and a warning level — whose physical instances live in `loadCarrierUnits`, one row per box per
 * shipment, an identity thrown away when the round ends. A transfer carrier is the opposite on
 * both counts: an individual with a durable label, and no balance at all, because a trolley is one
 * of itself and never a quantity of anything. So there is nothing here about stock, and no
 * dimensions either — nobody plans a load against a trolley, they put things on it.
 *
 * `number` is optional, and that is the whole point of it. Left empty,
 * `set_scoped_transfer_carrier_number` allocates the organisation's next one and somebody prints
 * the label. Filled in, the trigger keeps what was passed — which is how a fleet that was labelled
 * before the software existed is entered with the numbers already painted on it, the normal
 * starting condition rather than an edge case.
 *
 * `currentZoneId` is where the device stands now. It is editable because a trolley pushed
 * somewhere without a scan has to be correctable, but it is **not** authoritative for long:
 * finishing a put-away writes the zone that leg ended in, so a correction holds only until the
 * trolley is used again.
 */
export const transferCarrierFormSchema = z.object({
	name: ZodStringNullable,
	description: ZodStringNullable,
	number: z.preprocess(
		// An empty field arrives as "" from the input and has to become "allocate one" rather than
		// NaN, which is what `z.coerce.number()` would make of it.
		(value) => (value === "" || value === null || value === undefined ? null : value),
		z.coerce.number().int().positive().nullable(),
	),
	currentZoneId: ZodStringNullable,
});

export type TransferCarrierFormSchema = z.infer<typeof transferCarrierFormSchema>;
